import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { LoadingState, EmptyState } from "@/components/States";
import { Plus, PencilSimple, Trash, FloppyDisk, X } from "@phosphor-icons/react";

const empty = { date: "", fr: "", sr: "" };

export default function AdminPreviousResults() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // date being edited, or "new"
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/results");
      setRows(data);
    } catch (e) {
      toast.error("Failed to load results");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startNew = () => { setForm(empty); setEditing("new"); };
  const startEdit = (r) => { setForm({ date: r.date, fr: r.fr, sr: r.sr }); setEditing(r.date); };
  const cancel = () => { setEditing(null); setForm(empty); };

  const save = async () => {
    if (!form.date) { toast.error("Date is required"); return; }
    try {
      // preserve published state on edit; keep existing published if editing
      const existing = rows.find((r) => r.date === form.date);
      await api.post("/admin/results", {
        date: form.date,
        fr: form.fr.trim(),
        sr: form.sr.trim(),
        published: existing ? existing.published : true,
      });
      toast.success("Saved");
      cancel();
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (date) => {
    if (!window.confirm(`Delete result for ${date}?`)) return;
    try {
      await api.delete(`/admin/results/${date}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  return (
    <div data-testid="admin-previous-results">
      <div className="flex items-center justify-between mb-6 gap-3">
        <div>
          <h1 className="font-heading font-black text-2xl text-slate-900">Previous Result</h1>
          <p className="text-slate-500 text-sm mt-1">All results, newest first.</p>
        </div>
        <button onClick={startNew} data-testid="prev-add-btn" className="h-12 px-4 rounded-xl bg-brand text-white font-bold flex items-center gap-2 hover:bg-brand-hover transition-colors flex-shrink-0">
          <Plus className="w-5 h-5" weight="bold" /> <span className="hidden sm:inline">Add New</span>
        </button>
      </div>

      {editing && (
        <div className="bg-white rounded-2xl shadow-sm border border-brand/30 p-5 mb-6" data-testid="prev-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold text-slate-900">{editing === "new" ? "Add New Result" : `Edit ${editing}`}</h3>
            <button onClick={cancel} className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Date</label>
              <input type="date" value={form.date} disabled={editing !== "new"} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="prev-form-date" className="w-full h-14 px-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand/40 disabled:bg-slate-50" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">First Round</label>
              <input value={form.fr} onChange={(e) => setForm({ ...form, fr: e.target.value })} type="text" data-testid="prev-form-fr" className="w-full h-14 px-3 rounded-xl border border-slate-200 text-xl font-bold text-center focus:outline-none focus:ring-2 focus:ring-brand/40" placeholder="00" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Second Round</label>
              <input value={form.sr} onChange={(e) => setForm({ ...form, sr: e.target.value })} type="text" data-testid="prev-form-sr" className="w-full h-14 px-3 rounded-xl border border-slate-200 text-xl font-bold text-center focus:outline-none focus:ring-2 focus:ring-brand/40" placeholder="00" />
            </div>
          </div>
          <button onClick={save} data-testid="prev-form-save" className="mt-4 w-full sm:w-auto h-12 px-6 rounded-xl bg-brand text-white font-bold flex items-center justify-center gap-2 hover:bg-brand-hover transition-colors">
            <FloppyDisk className="w-5 h-5" weight="bold" /> Save
          </button>
        </div>
      )}

      {loading ? (
        <LoadingState />
      ) : rows.length === 0 ? (
        <EmptyState title="No results yet" subtitle="Add your first result." />
      ) : (
        <div className="space-y-3" data-testid="prev-list">
          {rows.map((r, i) => (
            <div key={r.date} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center gap-4" data-testid={`prev-item-${r.date}`}>
              <span className="text-slate-300 font-bold w-6 text-center flex-shrink-0">{i + 1}</span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-800 text-sm">{r.date}</p>
                <div className="flex gap-4 mt-1">
                  <span className="text-sm"><span className="text-slate-400 font-semibold">F/R</span> <span className="font-heading font-extrabold text-brand">{r.fr || "-"}</span></span>
                  <span className="text-sm"><span className="text-slate-400 font-semibold">S/R</span> <span className="font-heading font-extrabold text-slate-800">{r.sr || "-"}</span></span>
                  {!r.published && <span className="text-[11px] font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full self-center">Draft</span>}
                </div>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button onClick={() => startEdit(r)} data-testid={`prev-edit-${r.date}`} className="w-12 h-12 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center hover:bg-slate-200 transition-colors">
                  <PencilSimple className="w-5 h-5" weight="bold" />
                </button>
                <button onClick={() => remove(r.date)} data-testid={`prev-delete-${r.date}`} className="w-12 h-12 rounded-xl bg-red-50 text-red-600 flex items-center justify-center hover:bg-red-100 transition-colors">
                  <Trash className="w-5 h-5" weight="bold" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
